import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  LayoutDashboard, Users, Clock, ArrowRightLeft, Building2, FileText,
  LogOut, ChevronLeft, ChevronRight, Shield, Settings, Sun, Moon, Landmark, Menu, X, ClipboardCheck, Repeat,
  Activity, Wallet, Scale, ShieldAlert,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import { toast } from "sonner";
import useTransferAlerts from "@/hooks/useTransferAlerts";

export const companyNavItems = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/company/dashboard", permKey: "dashboard" },
  { label: "Colaboradores", icon: Users, path: "/company/employees", permKey: "colaboradores" },
  { label: "Usuários", icon: Shield, path: "/company/users", permKey: "usuarios" },
  { label: "Aprovações", icon: Clock, path: "/company/approvals", permKey: "aprovacoes" },
  { label: "Fechamento", icon: FileText, path: "/company/fechamento", permKey: "fechamento" },
  { label: "Pagamentos", icon: Wallet, path: "/company/payments", permKey: "pagamentos" },
  { label: "Histórico", icon: ArrowRightLeft, path: "/company/history", permKey: "historico" },
  { label: "Configurações", icon: Settings, path: "/company/settings", permKey: "configuracoes" },
];

export const adminNavItems = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/admin/dashboard", permKey: "dashboard" },
  { label: "Usuários", icon: Shield, path: "/admin/users", permKey: "usuarios" },
  { label: "Aprovações", icon: Clock, path: "/admin/approvals", permKey: "aprovacoes" },
  { label: "KYC Pendentes", icon: ShieldAlert, path: "/admin/kyc", permKey: "aprovacoes" },
  { label: "Solicitações", icon: ClipboardCheck, path: "/admin/pending-actions", permKey: "solicitacoes" },
  { label: "Credores", icon: Landmark, path: "/admin/credores", permKey: "credores" },
  { label: "Empresas", icon: Building2, path: "/admin/companies", permKey: "empresas" },
  { label: "Antecipações", icon: ArrowRightLeft, path: "/admin/anticipations", permKey: "antecipacoes" },
  { label: "Conciliação", icon: Scale, path: "/admin/reconciliation", permKey: "conciliacao" },
  { label: "Auditoria", icon: FileText, path: "/admin/audit", permKey: "auditoria" },
  { label: "Saúde do Sistema", icon: Activity, path: "/admin/monitoring", permKey: "monitoramento" },
];

export const Sidebar = ({ type = "company" }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [showAccounts, setShowAccounts] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, switchAccount } = useAuth();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    if (type === "admin") {
      api.get("/admin/pending-count").then(r => setPendingCount(r.data.count)).catch(() => {});
      const interval = setInterval(() => {
        api.get("/admin/pending-count").then(r => setPendingCount(r.data.count)).catch(() => {});
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [type]);

  // Badge para aba Pagamentos — conta transfers retryable (failed/blocked) do
  // mês corrente. Só roda quando user é company_admin e tem permissão.
  const canSeePagamentos = type === "company" && user?.role === "company_admin"
    && (user?.permissions?.pagamentos || "none") !== "none";
  const { count: failingTransfers } = useTransferAlerts({ enabled: canSeePagamentos });
  const allNavItems = type === "admin" ? adminNavItems : companyNavItems;
  const perms = user?.permissions || {};
  const navItems = allNavItems.filter(item => {
    if (!item.permKey) return true;
    // Master admins see everything
    if (user?.is_master) return true;
    // Non-master can't see master-only items
    if (item.requiresMaster) return false;
    // Restrictive default: permission must be explicitly granted
    const level = perms[item.permKey];
    if (!level) return false;
    return level !== "none";
  });

  const mfaBlocked = (type === "company" || type === "admin") && user?.mfa_blocked;

  const isActive = (path) => {
    if (path === "/company" || path === "/admin") return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const navContent = (
    <>
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo-kredz-dark.png" alt="Kredz" className="h-10 w-auto shrink-0" />
        </div>
        <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} className="lg:hidden h-8 w-8 text-muted-foreground">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <Separator className="bg-border" />

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <Button
            key={item.path}
            data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
            variant="ghost"
            onClick={() => {
              if (mfaBlocked) return;
              if (window.__unsavedChanges && !window.confirm("Você tem alterações não salvas. Deseja sair sem salvar?")) return;
              navigate(item.path);
            }}
            disabled={mfaBlocked && item.path !== "/company/dashboard" && item.path !== "/admin/dashboard"}
            className={`w-full justify-start gap-3 h-10 px-3 font-medium transition-colors relative ${
              mfaBlocked && item.path !== "/company/dashboard" && item.path !== "/admin/dashboard" ? "opacity-30 cursor-not-allowed" :
              isActive(item.path)
                ? "bg-blue-600/15 text-blue-400 hover:bg-blue-600/20 hover:text-blue-400"
                : "text-muted-foreground hover:bg-white/5 hover:text-foreground"
            } ${collapsed ? "justify-center px-0" : ""}`}
          >
            <item.icon className="w-4 h-4 shrink-0" />
            {!collapsed && item.label}
            {!collapsed && item.label === "Aprovações" && pendingCount > 0 && (
              <span className="ml-auto bg-yellow-500 text-black text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                {pendingCount}
              </span>
            )}
            {!collapsed && item.label === "Pagamentos" && failingTransfers > 0 && (
              <span
                data-testid="sidebar-payments-alert-badge"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/company/payments?tab=transfers&status=failed");
                }}
                title={`${failingTransfers} transferência(s) aguardando atenção no mês corrente — clique para ver só as falhadas`}
                className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 animate-pulse cursor-pointer hover:bg-red-400 transition-colors"
              >
                {failingTransfers > 99 ? "99+" : failingTransfers}
              </span>
            )}
            {collapsed && item.label === "Pagamentos" && failingTransfers > 0 && (
              <span
                data-testid="sidebar-payments-alert-dot"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/company/payments?tab=transfers&status=failed");
                }}
                title={`${failingTransfers} transferência(s) aguardando atenção`}
                className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse cursor-pointer"
              />
            )}
          </Button>
        ))}
      </nav>

      <div className="p-3 space-y-2">
        <Separator className="bg-border" />
        {!collapsed && user && (
          <div className="px-3 py-2">
            {user.account_label && (
              <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-400 mb-1" data-testid="sidebar-account-label">{user.account_label}</p>
            )}
            <p className="text-sm font-medium truncate">{user.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          </div>
        )}
        {!collapsed && (
          <Button
            data-testid="switch-account-btn"
            variant="ghost"
            onClick={async () => {
              if (showAccounts) { setShowAccounts(false); return; }
              try {
                const res = await api.get("/auth/accounts");
                setAccounts(res.data.accounts || []);
                setShowAccounts(true);
              } catch { /* silent */ }
            }}
            className={`w-full justify-start gap-3 h-10 px-3 text-muted-foreground hover:text-foreground hover:bg-white/5`}
          >
            <Repeat className="w-4 h-4 shrink-0" />
            Trocar Acesso
          </Button>
        )}
        {!collapsed && showAccounts && accounts.length > 0 && (
          <div className="space-y-1 px-1 animate-fade-in" data-testid="account-switcher">
            {accounts.map((acc) => {
              const isCurrent = acc.id === user?.account_id;
              const routes = { admin: "/admin/dashboard", company_admin: "/company/dashboard", employee: "/employee" };
              return (
                <button key={acc.id} data-testid={`switch-to-${acc.id}`}
                  disabled={isCurrent}
                  onClick={async () => {
                    try {
                      await switchAccount(acc.id);
                      const target = routes[acc.role] || "/";
                      window.location.href = target;
                    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao trocar perfil"); }
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${isCurrent ? "bg-blue-500/10 border border-blue-500/20 text-blue-400 cursor-default" : "hover:bg-white/5 text-muted-foreground hover:text-foreground"}`}>
                  <span className="font-medium">{acc.label}</span>
                  {isCurrent && <span className="ml-2 text-[10px] text-blue-400/70">(atual)</span>}
                </button>
              );
            })}
          </div>
        )}
        <Button
          data-testid="theme-toggle-btn"
          variant="ghost"
          onClick={toggleTheme}
          className={`w-full justify-start gap-3 h-10 px-3 text-muted-foreground hover:text-foreground hover:bg-white/5 ${collapsed ? "justify-center px-0" : ""}`}
        >
          {theme === "dark" ? <Sun className="w-4 h-4 shrink-0" /> : <Moon className="w-4 h-4 shrink-0" />}
          {!collapsed && (theme === "dark" ? "Tema Claro" : "Tema Escuro")}
        </Button>
        <Button
          data-testid="sidebar-logout-btn"
          variant="ghost"
          onClick={() => { logout(); navigate("/login"); }}
          className={`w-full justify-start gap-3 h-10 px-3 text-muted-foreground hover:text-red-400 hover:bg-red-500/10 ${collapsed ? "justify-center px-0" : ""}`}
        >
          <LogOut className="w-4 h-4 shrink-0" />
          {!collapsed && "Sair"}
        </Button>
        <Button
          data-testid="sidebar-toggle-btn"
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className="w-full h-8 text-muted-foreground hover:text-foreground hidden lg:flex"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <Button
        data-testid="mobile-menu-btn"
        variant="ghost" size="icon"
        onClick={() => setMobileOpen(true)}
        className="fixed top-4 left-4 z-50 lg:hidden h-10 w-10 bg-card border border-border shadow-lg"
      >
        <Menu className="w-5 h-5" />
      </Button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            data-testid="sidebar"
            className="absolute left-0 top-0 h-full w-64 bg-card border-r border-border flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {navContent}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div
        data-testid="sidebar"
        className={`fixed left-0 top-0 h-screen bg-card border-r border-border flex-col z-40 transition-[width] duration-200 hidden lg:flex ${collapsed ? "w-16" : "w-64"}`}
      >
        {navContent}
      </div>
    </>
  );
};
